import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, TrendingUp, CheckCircle2, AlertCircle, DollarSign, Download, FileSpreadsheet } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../utils/api';
import { toast } from 'sonner';

const KPIs = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKPIs();
  }, []);

  const fetchKPIs = async () => {
    try {
      const response = await api.get('/kpis');
      setKpis(response.data);
    } catch (error) {
      toast.error('Error al cargar KPIs');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#1E3A8A', '#D4AF37', '#10B981', '#F59E0B', '#EF4444'];

  const tareasAreaData = kpis
    ? Object.entries(kpis.tareas_por_area).map(([area, count]) => ({
        area,
        cantidad: count,
      }))
    : [];

  const leadsData = kpis
    ? Object.entries(kpis.leads_por_estado).map(([estado, count]) => ({
        name: estado,
        value: count,
      }))
    : [];

  const conversionRate = kpis
    ? ((kpis.propuestas_aceptadas / kpis.propuestas_totales) * 100).toFixed(1)
    : 0;

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/kpis/export/csv', {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kpis_peritum_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('KPIs exportados a CSV');
    } catch (error) {
      toast.error('Error al exportar CSV');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await api.get('/kpis/export/excel', {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_kpis_peritum_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Reporte completo exportado a Excel');
    } catch (error) {
      toast.error('Error al exportar Excel');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="w-10 h-10 text-primary" strokeWidth={1.5} />
                  <h1 className="text-4xl font-bold text-slate-900" data-testid="kpis-title">
                    Panel de KPIs
                  </h1>
                </div>
                <p className="text-lg text-slate-600">
                  Métricas clave de tu operación
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleExportCSV}
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4" />
                  Exportar CSV
                </Button>
                <Button
                  onClick={handleExportExcel}
                  className="flex items-center gap-2 bg-primary hover:bg-primary/90"
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Exportar Excel
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Top Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="p-6 border-slate-200" data-testid="kpi-card-tareas-completadas">
                  <div className="flex items-start justify-between mb-3">
                    <div className="bg-green-50 p-3 rounded-lg">
                      <CheckCircle2 className="w-6 h-6 text-green-600" strokeWidth={1.5} />
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Tareas Completadas</p>
                  <p className="text-3xl font-bold text-slate-900">{kpis?.tareas_completadas || 0}</p>
                </Card>

                <Card className="p-6 border-slate-200" data-testid="kpi-card-tareas-pendientes">
                  <div className="flex items-start justify-between mb-3">
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <AlertCircle className="w-6 h-6 text-orange-600" strokeWidth={1.5} />
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Tareas Pendientes</p>
                  <p className="text-3xl font-bold text-slate-900">{kpis?.tareas_pendientes || 0}</p>
                </Card>

                <Card className="p-6 border-slate-200" data-testid="kpi-card-proyeccion-ingresos">
                  <div className="flex items-start justify-between mb-3">
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <DollarSign className="w-6 h-6 text-accent" strokeWidth={1.5} />
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Proyección Ingresos</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${new Intl.NumberFormat('es-CL').format(kpis?.proyeccion_ingresos || 0)}
                  </p>
                </Card>

                <Card className="p-6 border-slate-200" data-testid="kpi-card-conversion-propuestas">
                  <div className="flex items-start justify-between mb-3">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Conversión Propuestas</p>
                  <p className="text-3xl font-bold text-slate-900">{conversionRate}%</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {kpis?.propuestas_aceptadas || 0} de {kpis?.propuestas_totales || 0}
                  </p>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Tareas por Área */}
                <Card className="p-6 border-slate-200">
                  <h3 className="text-xl font-semibold text-slate-900 mb-6">
                    Tareas por Área
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={tareasAreaData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="area" stroke="#64748B" style={{ fontSize: '12px' }} />
                      <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar dataKey="cantidad" fill="#1E3A8A" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                {/* Leads por Estado */}
                <Card className="p-6 border-slate-200">
                  <h3 className="text-xl font-semibold text-slate-900 mb-6">
                    Leads por Estado
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={leadsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {leadsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-6 border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-600 mb-3">Propuestas</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-700">Total</span>
                      <span className="font-bold text-slate-900">{kpis?.propuestas_totales || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-700">Aceptadas</span>
                      <span className="font-bold text-green-600">{kpis?.propuestas_aceptadas || 0}</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-600 mb-3">Leads</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-700">Tasa de Conversión</span>
                      <span className="font-bold text-slate-900">{kpis?.tasa_conversion_leads || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-700">Total Leads</span>
                      <span className="font-bold text-slate-900">
                        {Object.values(kpis?.leads_por_estado || {}).reduce((a, b) => a + b, 0)}
                      </span>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border-slate-200 bg-primary/5">
                  <h4 className="text-sm font-semibold text-slate-600 mb-3">Finanzas</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-700">Ingresos Proyectados</span>
                      <span className="font-bold text-accent">
                        ${new Intl.NumberFormat('es-CL', { notation: 'compact' }).format(
                          kpis?.proyeccion_ingresos || 0
                        )}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default KPIs;
